import type { MedplumClient } from '@medplum/core';
import type { Bundle, Goal, QuestionnaireResponse } from '@medplum/fhirtypes';
import { TEMPLATE_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import { getCarePlanEpisodeId, isTreatmentPlanCarePlan } from '@unify/fhir-model/resources/treatment-plan-careplan';
import { getEpisodeIdFromQr, hasTreatmentPlanningTag } from './activeTreatmentPlanDiagnoses';

/**
 * Mark treatment-plan QuestionnaireResponses and CarePlans for an episode as stopped/completed
 * so they no longer appear as active plans (e.g. after EpisodeOfCare discharge).
 */
export async function stopTreatmentPlansForEpisode(
  medplum: MedplumClient,
  episodeId: string,
  patientId: string,
): Promise<QuestionnaireResponse[]> {
  const bundle = await medplum.search('QuestionnaireResponse', {
    subject: `Patient/${patientId}`,
    _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
    _count: '50',
    _sort: '-authored',
  });

  const responses = (bundle.entry ?? [])
    .map((e) => e.resource as QuestionnaireResponse | undefined)
    .filter((qr): qr is QuestionnaireResponse => Boolean(qr?.id));

  const toStop = responses.filter((qr) => {
    if (!hasTreatmentPlanningTag(qr)) return false;
    if (getEpisodeIdFromQr(qr) !== episodeId) return false;
    return qr.status !== 'stopped' && qr.status !== 'entered-in-error';
  });

  const updated: QuestionnaireResponse[] = [];
  for (const qr of toStop) {
    const next = await medplum.updateResource<QuestionnaireResponse>({
      ...qr,
      status: 'stopped',
    });
    updated.push(next);
  }

  const plans = await medplum.searchResources('CarePlan', {
    subject: `Patient/${patientId}`,
    _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
    _count: '50',
  });
  for (const plan of plans) {
    if (!plan.id || !isTreatmentPlanCarePlan(plan)) continue;
    if (getCarePlanEpisodeId(plan) !== episodeId) continue;
    if (plan.status === 'completed' || plan.status === 'entered-in-error') continue;

    const goalIds = (plan.goal ?? [])
      .map((reference) => reference.reference?.replace(/^Goal\//, ''))
      .filter((id): id is string => Boolean(id));
    const goals = await Promise.all(
      goalIds.map((id) => medplum.readResource('Goal', id)),
    );
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [
        {
          resource: { ...plan, status: 'completed' },
          request: { method: 'PUT', url: `CarePlan/${plan.id}` },
        },
        ...goals.map((goal: Goal) => ({
          resource: { ...goal, lifecycleStatus: 'completed' as const },
          request: { method: 'PUT' as const, url: `Goal/${goal.id}` },
        })),
      ],
    };
    await medplum.executeBatch(bundle);
  }

  return updated;
}
